// The Mountain orbit — shared by LIFE's two views. `useOrbit` owns the
// camera angle (drag, snap-to-face, D-pad turns, idle spin); `OrbitBackdrop`
// paints the current frame full-bleed and dissolves its flat sky into the
// page's starry night so the mountain sits in space, not in a box. The
// compass overlays this; the Mountain view overlays its paths and controls.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Image, PanResponder, StyleSheet, View, type GestureResponderHandlers } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import type { Sector } from '../domain/olmps';
import { ORBIT_FRAMES } from './orbitFrames';
import { useV10 } from './theme';

export const SECTORS: Sector[] = ['S', 'H', 'A', 'P', 'E'];
const N = ORBIT_FRAMES.length;
export const FACE = 72;
const IDLE_DEG_PER_SEC = 5;
const DRAG_PX_PER_DEG = 2.2;

export interface Orbit {
  frame: number;
  frontDeg: number;
  frontSector: Sector;
  settled: boolean; // not turning, not spinning
  spinning: boolean;
  turn: (dir: 1 | -1) => void;
  snapToFace: (idx: number) => void;
  toggleSpin: () => void;
  panHandlers: GestureResponderHandlers;
}

export function useOrbit(startSector: Sector | null): Orbit {
  const startIdx = startSector ? SECTORS.indexOf(startSector) : 0;
  const deg = useRef(startIdx * FACE);
  const target = useRef<number | null>(null);
  const [frame, setFrame] = useState(Math.round((startIdx * FACE) / (360 / N)) % N);
  const [spinning, setSpinning] = useState(false);
  const [turning, setTurning] = useState(false);
  const spinRef = useRef(false);
  const dragging = useRef(false);
  const lastFrame = useRef(frame);

  const pushFrame = () => {
    const f = ((Math.round(deg.current / (360 / N)) % N) + N) % N;
    if (f !== lastFrame.current) {
      lastFrame.current = f;
      setFrame(f);
    }
  };
  useEffect(() => {
    let raf = 0;
    let prev = performance.now();
    const tick = (now: number) => {
      const dt = Math.min(0.05, (now - prev) / 1000);
      prev = now;
      if (!dragging.current) {
        if (target.current != null) {
          const diff = ((target.current - deg.current + 540) % 360) - 180;
          if (Math.abs(diff) < 0.4) {
            deg.current = target.current;
            target.current = null;
            setTurning(false);
          } else {
            deg.current = (deg.current + diff * Math.min(1, dt * 7) + 360) % 360;
          }
          pushFrame();
        } else if (spinRef.current) {
          deg.current = (deg.current + IDLE_DEG_PER_SEC * dt) % 360;
          pushFrame();
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const snapToFace = (idx: number) => {
    target.current = ((idx % 5) + 5) % 5 * FACE;
    setTurning(true);
  };
  const frontIdxNow = () => ((Math.round(deg.current / FACE) % 5) + 5) % 5;
  const turn = (dir: 1 | -1) => {
    spinRef.current = false;
    setSpinning(false);
    const base = target.current != null ? Math.round(target.current / FACE) : frontIdxNow();
    snapToFace(base + dir);
  };
  const toggleSpin = () => {
    const on = !spinRef.current;
    spinRef.current = on;
    setSpinning(on);
    if (!on) snapToFace(frontIdxNow());
  };

  const startDeg = useRef(0);
  const pan = useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: (_e, g) => Math.abs(g.dx) > 6 && Math.abs(g.dx) > Math.abs(g.dy),
      onPanResponderGrant: () => {
        dragging.current = true;
        target.current = null;
        spinRef.current = false;
        setSpinning(false);
        setTurning(true);
        startDeg.current = deg.current;
      },
      onPanResponderMove: (_e, g) => {
        deg.current = (startDeg.current - g.dx / DRAG_PX_PER_DEG + 3600) % 360;
        pushFrame();
      },
      onPanResponderRelease: () => {
        dragging.current = false;
        snapToFace(frontIdxNow());
      },
      onPanResponderTerminate: () => {
        dragging.current = false;
        snapToFace(frontIdxNow());
      },
    }),
  ).current;

  const frontDeg = (frame * 360) / N;
  const frontSector = SECTORS[((Math.round(frontDeg / FACE) % 5) + 5) % 5];
  return { frame, frontDeg, frontSector, settled: !turning && !spinning, spinning, turn, snapToFace, toggleSpin, panHandlers: pan.panHandlers };
}

/** Deterministic star field for the night sky. */
function Stars({ width, height }: { width: number; height: number }) {
  const stars = useMemo(() => {
    const out: { x: number; y: number; r: number; o: number }[] = [];
    let seed = 7;
    const rnd = () => {
      seed = (seed * 9301 + 49297) % 233280;
      return seed / 233280;
    };
    for (let i = 0; i < 90; i++) out.push({ x: rnd() * width, y: rnd() * height * 0.75, r: 0.6 + rnd() * 1.4, o: 0.25 + rnd() * 0.6 });
    return out;
  }, [width, height]);
  return (
    <View pointerEvents="none" style={StyleSheet.absoluteFill}>
      {stars.map((s, i) => (
        <View key={i} style={{ position: 'absolute', left: s.x, top: s.y, width: s.r * 2, height: s.r * 2, borderRadius: s.r, backgroundColor: `rgba(243,234,215,${s.o.toFixed(2)})` }} />
      ))}
    </View>
  );
}

/** Full-bleed Mountain: the square orbit frame at `top`, its flat sky
 *  feathered into the page's night gradient above and the mist below. */
export function OrbitBackdrop({ frame, width, height, top }: { frame: number; width: number; height: number; top: number }) {
  const t = useV10();
  const dark = t.mode === 'dark';
  // The frames are night footage; in Day mode we lift them with an ivory veil.
  const sky = dark ? ['#070b17', '#0f1a3a', '#22325f'] : ['#dfe6f3', '#c9d6ee', '#b7c8e8'];
  const page = dark ? '#070b17' : t.night;
  return (
    <View pointerEvents="none" style={StyleSheet.absoluteFill}>
      <LinearGradient colors={sky as [string, string, string]} locations={[0, 0.35, 0.6]} style={StyleSheet.absoluteFill} />
      {dark ? <Stars width={width} height={height} /> : null}
      <View style={{ position: 'absolute', left: 0, top, width, height: width, overflow: 'hidden' }}>
        {ORBIT_FRAMES.map((src, i) => (
          <Image key={i} source={src} style={{ position: 'absolute', left: 0, top: 0, width, height: width, opacity: i === frame ? 1 : 0 }} resizeMode="cover" />
        ))}
        {/* Feather: flat frame sky → page sky (top), mist → page (bottom). */}
        <LinearGradient colors={[sky[2], `${sky[2]}00`]} style={{ position: 'absolute', left: 0, right: 0, top: 0, height: width * 0.42 }} />
        <LinearGradient colors={[`${page}00`, page]} style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: width * 0.3 }} />
        <LinearGradient colors={[sky[2], `${sky[2]}00`]} start={{ x: 0, y: 0.5 }} end={{ x: 1, y: 0.5 }} style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: width * 0.12 }} />
        <LinearGradient colors={[`${sky[2]}00`, sky[2]]} start={{ x: 0, y: 0.5 }} end={{ x: 1, y: 0.5 }} style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: width * 0.12 }} />
      </View>
      {/* Below the frame: page colour down to the nav. */}
      <View style={{ position: 'absolute', left: 0, right: 0, top: top + width, bottom: 0, backgroundColor: page }} />
      {!dark ? <View style={[StyleSheet.absoluteFill, { backgroundColor: 'rgba(244,238,224,0.35)' }]} /> : null}
    </View>
  );
}
